/**
 * The hidden treasures -- the visible half of the "seek" row in
 * shared/happy.js, drawn from the FINDS table and the server's word about
 * which of them THIS cat can currently find.
 *
 * Authority sits exactly where the prey put it: the server judges every
 * find (server/src/finds.js) and this file renders.  What is different is
 * WHAT arrives on the wire.  Prey positions stream twenty times a second
 * because mice move; treasures never move, so the client builds them
 * straight from the shared table -- the butterflies' one-number bargain,
 * the thing you can see and the thing you can find are the same
 * coordinates -- and the only wire traffic is the owner-only `finds` state
 * vector saying which rows to draw.  With no server there is no state and
 * nothing is drawn: the offline artifact page keeps its butterflies, and
 * treasures stay a feature of the shared street, exactly like the mice.
 *
 * THE GLINT is the "hidden, but fair" contract's client half.  Each
 * findable treasure shows a small turning spark from HINT_R metres, so a
 * child sweeping an area is handed a direction instead of a pixel-hunt;
 * the spark swells as the cat closes and drops low and quick inside the
 * find radius itself, which is the "you're on it -- stay put" cue for the
 * server's 1.2 s dig.  The treasure body underneath is drawn whenever the
 * state says it is there, glint range or not: an eagle-eyed player who
 * spots a pink hair tie from the porch rail earned it.
 *
 * Scene bargain, prey.js's verbatim: not built through level/builder.js
 * (these must toggle per-cat at runtime; the level merge is forever), its
 * own object3D beside the level, MeshBasicMaterial with a baked vertical
 * gradient (unlit, because half these spots are exactly the dark corners
 * the sun pass never favours).  Cost: one draw call per treasure body --
 * seven tiny geometries are not worth an instancing scheme that would force
 * them all to share one shape -- plus ONE InstancedMesh for all glints.
 *
 * SELF-DRIVEN: nothing in main.js's render loop calls into this file.  The
 * glint mesh's onBeforeRender hook runs every frame (it is always submitted
 * -- frustumCulled false, instance count fixed), and that is the whole
 * animation budget: a clock read and eight matrices.  Chosen over a
 * main.js update call so this feature's wiring stays in one block there,
 * beside the one net handler it needs.
 */
import * as THREE from "three";
import { FINDS } from "@catworld/shared";
import { FIND_STATE } from "@catworld/shared";

/**
 * Where the glint becomes visible, metres.  6 m spans "the area I am
 * poking around in" -- roughly one garden -- without carrying across the
 * street: farther and the sparks would advertise every treasure from the
 * spawn and the hiding would be theatre; nearer and a sweep could walk
 * past the bin lane's mouth without being told the lane holds anything.
 */
const HINT_R = 6.0;

/** Inside this the glint sits low and spins quick -- "dig here".  Matches
    the server's FIND_R by eye rather than by import (0.9 there): the cue
    is presentation and may be a shade generous, but a cue that lit up
    OUTSIDE the real radius would teach the wrong spot, so it stays at or
    under the server's number. */
const NEAR_R = 0.9;

/** Warm candle-white, unlit: reads as "small shiny thing catching the
    light" against both midday grass and the fog band, without being any
    treasure's own colour. */
const GLINT_COLOUR = 0xfff0b8;

/** Bake a belly-dark vertical gradient into vertex colour -- prey.js's
    shade(), for prey.js's reason: unlit material, but still a form. */
function shade(geom, lo, hi) {
  const pos = geom.attributes.position;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, Math.max(0, (pos.getY(i) - lo) / (hi - lo)));
    const v = 0.6 + 0.4 * t;
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = v;
  }
  geom.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return geom;
}

/**
 * One small geometry per treasure id, each a single mesh -- the whole
 * bestiary is "would a six-year-old name it from a metre away", which
 * silhouettes and colour carry better than triangle counts would.  Sizes
 * are honest-small (a real bottle cap, a real coin): the GLINT does the
 * long-range work, so the bodies are allowed to actually be treasures
 * rather than beacons.
 */
function geometryFor(id) {
  switch (id) {
    case "hairtie": {
      const g = new THREE.TorusGeometry(0.035, 0.012, 6, 10);
      g.rotateX(Math.PI / 2);          // lying flat on the walk
      g.translate(0, 0.014, 0);
      return shade(g, 0, 0.028);
    }
    case "coin": {
      const g = new THREE.CylinderGeometry(0.021, 0.021, 0.006, 12);
      g.translate(0, 0.004, 0);
      return shade(g, 0, 0.008);
    }
    case "feather": {
      // A slender vane: two quadratic lobes about a straight quill line.
      const s = new THREE.Shape();
      s.moveTo(0, -0.055);
      s.quadraticCurveTo(0.02, -0.01, 0.006, 0.055);
      s.lineTo(0, 0.06);
      s.lineTo(-0.006, 0.055);
      s.quadraticCurveTo(-0.02, -0.01, 0, -0.055);
      const g = new THREE.ShapeGeometry(s, 5);
      g.rotateX(-Math.PI / 2);         // flat
      g.rotateZ(0.35);                 // dropped at an angle, not placed
      g.translate(0, 0.012, 0);
      return shade(g, 0, 0.03);
    }
    case "bottlecap": {
      const g = new THREE.CylinderGeometry(0.024, 0.02, 0.009, 9);
      g.translate(0, 0.006, 0);
      return shade(g, 0, 0.012);
    }
    case "jinglyball": {
      const g = new THREE.SphereGeometry(0.035, 8, 6);
      g.translate(0, 0.033, 0);
      return shade(g, 0, 0.066);
    }
    case "catmouse": {
      const g = new THREE.SphereGeometry(0.05, 8, 6);
      g.scale(0.62, 0.55, 1.0);        // the toy is mouse-shaped: an ellipsoid
      g.translate(0, 0.026, 0);
      return shade(g, 0, 0.052);
    }
    case "snailshell":
    default: {
      const g = new THREE.SphereGeometry(0.032, 8, 6);
      g.scale(1, 0.82, 1);
      g.translate(0, 0.025, 0);
      return shade(g, 0, 0.05);
    }
  }
}

/** Treasure body colours by id.  Saturated on purpose: these are toys and
    trinkets in a toon garden, and a tasteful grey treasure is a pebble. */
const COLOURS = {
  hairtie: 0xd0608a,     // pink elastic
  coin: 0xe8c34a,        // gold
  feather: 0x6b9bd1,     // jay blue
  bottlecap: 0xc0392b,   // fizzy red
  jinglyball: 0xe2833c,  // the HUD accent's orange, the butterflies' joke
  catmouse: 0x93a084,    // catnip-stuffed grey-green
  snailshell: 0xa07a4f,  // banded brown
};

export class Finds {
  /** @param playerPos () => {x,y,z} -- the LOCAL player's live position,
      for glint proximity.  A getter, not a vector, because the local cat
      is drawn frames ahead of its own snapshot and the glint must agree
      with the paws, not the wire. */
  constructor({ playerPos }) {
    this.playerPos = playerPos;
    this.group = new THREE.Group();

    // No state until the server says: nothing drawn.  See the header.
    this.state = null;

    this.items = FINDS.map((f) => {
      const mat = new THREE.MeshBasicMaterial({
        vertexColors: true, side: THREE.DoubleSide, color: COLOURS[f.id] ?? 0xcccccc,
      });
      const mesh = new THREE.Mesh(geometryFor(f.id), mat);
      mesh.position.set(f.x, f.y, f.z);
      // A fixed pseudo-random yaw per row so no two treasures face the same
      // way; hashed from the position rather than rolled, so every client
      // and every reload agrees on how the feather lies.
      mesh.rotation.y = (Math.abs(f.x * 7.13 + f.z * 3.71) % (Math.PI * 2));
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      // Centimetre geometry, metre-scale scene: honest bounds would cull a
      // visible treasure at the frustum's edge (the butterflies' argument),
      // and seven never-culled draws are cheaper than fixing that.
      mesh.frustumCulled = false;
      mesh.visible = false;
      this.group.add(mesh);
      return mesh;
    });

    // All glints in one InstancedMesh.  Octahedron: reads as "spark" from
    // any side, and its silhouette changing as it turns IS the sparkle --
    // no shader, no texture, no additive pass.
    this.glint = new THREE.InstancedMesh(
      new THREE.OctahedronGeometry(0.045),
      new THREE.MeshBasicMaterial({ color: GLINT_COLOUR }),
      FINDS.length,
    );
    this.glint.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.glint.frustumCulled = false;
    this.glint.castShadow = false;
    this.glint.receiveShadow = false;
    this.zero = new THREE.Matrix4().makeScale(0, 0, 0);
    for (let i = 0; i < FINDS.length; i++) this.glint.setMatrixAt(i, this.zero);
    this.group.add(this.glint);

    // Reduced motion: the glint must STAY (it is how a treasure is found,
    // the butterflies' "calm, never gone" rule) -- it just turns and bobs
    // at half speed.
    this.calm = false;
    try {
      this.calm = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    } catch {}

    // The self-drive -- see the header.  onBeforeRender fires once per
    // frame for a submitted mesh, which the glint always is.
    this.dummy = new THREE.Object3D();
    this.glint.onBeforeRender = () => this.animate();
  }

  /** Add this to the scene; it is not part of the level build. */
  get object3D() {
    return this.group;
  }

  /** The owner's state off the wire: one FIND_STATE per FINDS row.  Trusted
      the way every server message is; the guards are shape, not suspicion. */
  setState(f) {
    if (!Array.isArray(f)) return;
    this.state = f;
    for (let i = 0; i < this.items.length; i++) {
      // Drawn unless TAKEN: OUT and BACK both mean "out there for you", and
      // an unknown value from a newer server reads as drawn -- wrongly
      // hiding a treasure is the only harmful failure here.
      this.items[i].visible = f[i] !== FIND_STATE.TAKEN;
    }
  }

  animate() {
    const p = this.playerPos?.();
    const t = performance.now() / 1000;
    const rate = this.calm ? 0.5 : 1;
    for (let i = 0; i < FINDS.length; i++) {
      const f = FINDS[i];
      if (!this.items[i].visible || !p) {
        this.glint.setMatrixAt(i, this.zero);
        continue;
      }
      const d = Math.hypot(p.x - f.x, p.y - f.y, p.z - f.z);
      if (d > HINT_R) {
        this.glint.setMatrixAt(i, this.zero);
        continue;
      }
      const near = d < NEAR_R;
      // Swell toward the cat: barely-there at the rim of the hint radius,
      // full-size close in.  The curve (sqrt-ish) front-loads the growth so
      // crossing into range is itself a visible event.
      const s = 0.35 + 0.65 * Math.pow(1 - d / HINT_R, 0.6);
      const spin = t * (near ? 6 : 2.2) * rate + i * 1.7;
      const bob = near ? 0.02 : Math.sin(t * 1.6 * rate + i) * 0.04;
      // Low over the treasure when "on it" (look DOWN, it is at your paws),
      // hovering at cat-eye height while it is still a direction to walk.
      const hover = near ? 0.10 : 0.30;
      const d3 = this.dummy;
      d3.position.set(f.x, f.y + hover + bob, f.z);
      d3.rotation.set(0, spin, 0);
      d3.scale.setScalar(s * (near ? 0.8 : 1));
      d3.updateMatrix();
      this.glint.setMatrixAt(i, d3.matrix);
    }
    this.glint.instanceMatrix.needsUpdate = true;
  }
}
